import { PolicyCreateDto, UserQueryDto } from '@bom-nextgen-keycloak/models';
import {
  useAlertMessage,
  useNotification,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  createPolicy,
  ErrorMessage,
  fetchUser,
  QUERY_KEY,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  AutoCompleteOption,
  ButtonRounded,
  DropdownField,
  StyledDialogTitle,
  TextField,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  MESSAGE_NO_BEGINNING_SPACE_REGEX,
  NO_BEGINNING_SPACE_REGEX,
  useDebounce,
} from '@bom-nextgen-keycloak/web/shared/util';
import {
  CircularProgress,
  MenuItem,
  TextField as MatTextField,
} from '@mui/material';
import Autocomplete, {
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { FC, Fragment, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import * as yup from 'yup';
import { TableForm } from '../TableForm';
import { StyledDialog } from './UserFormDialog.styled';

type UserFormDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
};

type UserForm = {
  name: string;
  description: string;
  type: string;
  logic: string;
  users: AutoCompleteOption[];
};

const validationSchema = yup.object({
  name: yup
    .string()
    .matches(NO_BEGINNING_SPACE_REGEX, MESSAGE_NO_BEGINNING_SPACE_REGEX)
    .required('Name is required'),
  users: yup.array().min(1, 'Users is required'),
  logic: yup.string().required('Logic is required'),
});

const initialValues: UserForm = {
  name: '',
  description: '',
  users: [],
  logic: 'POSITIVE',
  type: 'user',
};

const UserFormDialog: FC<UserFormDialogProps> = ({ onClose, onSave, open }) => {
  const { setAlertMessage } = useAlertMessage();
  const { setNotification } = useNotification();
  const resource = useResource();

  const [params] = useState<UserQueryDto>({
    search: '',
    first: 0,
    max: 25,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQueryTerm = useDebounce(searchQuery, 700);

  const { isLoading, data: optionUser = [] } = useQuery(
    [QUERY_KEY.USER, params, debouncedQueryTerm],
    async () => {
      const users = await fetchUser({
        ...params,
        search: searchQuery,
      });
      return users.items.map((user) => ({
        label: user.username || '',
        value: user,
      }));
    }
  );

  const onInputChange = (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setSearchQuery(value);
  };

  const createPolicyMutation = useMutation(createPolicy, {
    onSuccess: () => {
      setNotification({
        message: 'User-based policy created',
        type: 'success',
      });
      onSave();
    },
    onError: (error: AxiosError<ErrorMessage>) => {
      const message = error.response?.data.message || 'Cannot create policy';
      setAlertMessage({
        message:
          error.response?.status === 409
            ? 'Name already exists. try another name'
            : message,
        typeStatusMessage: 'error',
        statusCode: error.response?.status,
      });
    },
  });

  const handleDelateUser = (
    data: AutoCompleteOption,
    setFieldValue: (field: string, value: any) => void,
    users: AutoCompleteOption[]
  ) => {
    if (data) {
      const newUser = users.filter((val) => val.value.id !== data.value.id);
      setFieldValue('users', newUser);
    }
  };

  return (
    <StyledDialog
      onClose={() => onClose()}
      aria-labelledby="dialog-title"
      open={open}
    >
      <StyledDialogTitle id="dialog-title">
        Add user-based policy
      </StyledDialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={(
          values: UserForm,
          { setSubmitting }: FormikHelpers<UserForm>
        ) => {
          const payload: PolicyCreateDto = {
            logic: values.logic,
            name: values.name,
            description: values.description,
            type: values.type,
            decisionStrategy: 'UNANIMOUS',
            users: values.users.map((val) => val.value?.id || ''),
          };
          createPolicyMutation.mutate({ clientId: resource.clientId, payload });
          setSubmitting(false);
        }}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          isSubmitting,
          isValid,
          dirty,
          setFieldValue,
          values,
          getFieldMeta,
          setFieldTouched,
        }) => (
          <Form>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <TextField name="name" label="Name" required />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField name="description" label="Description" multiline />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  id="policy"
                  filterSelectedOptions
                  options={optionUser}
                  multiple
                  getOptionLabel={(option) => option.label}
                  limitTags={3}
                  value={values.users}
                  onChange={(e, value) => {
                    setFieldValue('users', value || []);
                  }}
                  onInputChange={onInputChange}
                  loading={isLoading}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  renderInput={(inputParams) => (
                    <MatTextField
                      {...inputParams}
                      id="users"
                      name="users"
                      label="Users"
                      fullWidth
                      placeholder="Enter username"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      required={values.users.length === 0}
                      variant="standard"
                      onBlur={() => {
                        setFieldTouched('users');
                      }}
                      error={
                        getFieldMeta('users').touched &&
                        !!getFieldMeta('users').error
                      }
                      helperText={
                        getFieldMeta('users').touched &&
                        getFieldMeta('users').error
                      }
                      InputProps={{
                        ...inputParams.InputProps,
                        endAdornment: (
                          <Fragment>
                            {isLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {inputParams.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
              {values.users.length !== 0 && (
                <Box sx={{ mb: 2 }}>
                  <TableForm
                    data={values.users}
                    columnName="Username"
                    tooltipTitle="Remove user"
                    onClickDelate={(data) =>
                      handleDelateUser(data, setFieldValue, values.users)
                    }
                  />
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <DropdownField
                  name="logic"
                  label="Logic"
                  placeholder="please enter your logic"
                  required
                  variant="standard"
                >
                  <MenuItem value="POSITIVE">Positive</MenuItem>
                  <MenuItem value="NEGATIVE">Negative</MenuItem>
                </DropdownField>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                color="secondary"
                onClick={() => onClose()}
                variant="text"
              >
                Cancel
              </Button>
              <ButtonRounded
                disabled={!(isValid && dirty) || isSubmitting}
                loading={createPolicyMutation.isLoading}
                type="submit"
                variant="text"
              >
                Save
              </ButtonRounded>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </StyledDialog>
  );
};

export { UserFormDialog };
